import { useTranslation } from '@mukil/i18n';
import type { AuthErrorDisplay } from '../../lib/auth/auth_error_display.js';

/**
 * The single error line under an auth form — `/signup` and `/login` both
 * render this, replacing the two hand-rolled `<p role="alert">{message}</p>`
 * copies that each printed a caught rejection's raw `.message`.
 *
 * That raw message is only ever appropriate for a rejection the SERVER
 * produced. A transport failure has no server message to show — it surfaced
 * the browser's own `fetch` TypeError ("Failed to fetch" on Android/Chromium,
 * "Load failed" on iOS/WKWebView) as untranslated red copy in the slot
 * reserved for "what you typed is wrong", telling the user nothing about the
 * only thing that would actually help: the request never left for, or never
 * reached, `apps/api`. `classifyAuthError` (`lib/auth/auth_error_display.ts`)
 * makes that distinction; this component owns the copy for the two branches
 * that need it, so both are translated like every other user-visible string.
 *
 * One component rather than a helper called from each screen, per
 * `docs/design-system/CLAUDE.md`'s "one canonical component per concept" —
 * the same reasoning as the sibling `AuthAltActionLink`. All three auth
 * surfaces use it: signup, login, and the ESS invite-accept screen.
 *
 * `text-[13px]` deliberately matches `FormField`'s own error `<p>`
 * (`packages/jumpstone-ui-react/src/components/form_field/form_field.tsx`) —
 * this line renders directly beneath field errors, and at `text-sm` the two
 * adjacent red alert lines sat at different sizes.
 */
export function AuthFormError({
  error,
}: {
  error: AuthErrorDisplay;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <p role="alert" className="text-[13px] text-[var(--color-danger)]">
      {authErrorCopy(error, t)}
    </p>
  );
}

/**
 * Split out so each branch calls `t` with a STRING LITERAL key — that is what
 * `pnpm i18n:lint`'s scanner extracts, and a `t(cond ? a : b)` would be
 * invisible to it (CLAUDE.md § i18n).
 */
function authErrorCopy(
  error: AuthErrorDisplay,
  t: (key: string) => string,
): string {
  switch (error.kind) {
    case 'server':
      return error.message;
    case 'network':
      return t('auth.network_error');
    case 'unknown':
      return t('auth.unknown_error');
    case 'signup_account_exists':
      return t('signup.account_exists_error');
  }
}
